import { Controller } from "@hotwired/stimulus"
import { getJSON } from "../api.js"

/*
  Combobox / autocomplete z chipsami (badge). Jeden kontroler, trzy tryby:
  - pojedynczy wybór (województwo, powiat) → 1 chip zastępuje input, 1 hidden input
  - multiselect (podkategorie)             → N chipsów, N hidden inputów o tej samej
    nazwie (Django: request.POST.getlist("subcategories"))
  - zależność powiat → województwo: data-combobox-depends-on-value="voivodeship";
    opcje zależnego pola mają "group" = value rodzica. Zmiana rodzica czyści wybór.
  - tryb zdalny (url value): wpisanie >= minChars znaków odpytuje API z debounce 200 ms;
    gdy dependsOn jest ustawiony, wartość rodzica trafia do URL jako parametr;
    guard sekwencji odrzuca odpowiedzi z przeterminowanych fetchów.
  - tryb free-text (freeText value): sugestie z optionsValue lub URL; wpisany tekst
    jest wartością (nie PK); brak chipsów; wartości spoza listy są dozwolone.
    Przeznaczony dla pola city_text — formularz submittuje to co wpisał user.

  Kontrakt opcji (JSON w data-combobox-options-value lub odpowiedź API):
    [{ "value": 1, "label": "mazowieckie", "meta": "8 pow.", "group": null }]
  API może zwrócić { id, name, meta?, group? } — normalizowane do kontraktu.

  Klawiatura: ↑/↓ nawigacja, Enter wybór, Esc zamyka, Backspace (pusty input)
  usuwa ostatni chip.

  Emituje zdarzenie "combobox:change" na window — do wieszania kolejnych zależności
  (np. wizard nasłuchuje podkategorii → odświeża hint cenowy).

  selectValues(values) — publiczny setter do preselekcji z zewnątrz (np. modal edycji
  wypełniany danymi z API): zastępuje wybór po wartościach, bez ręcznego budowania opcji.
  Działa tylko w trybie opcji lokalnych (nie remote), mapuje PK na optionsValue.
*/
export default class extends Controller {
  static targets = ["box", "input", "list", "chips", "hidden"]
  static values = {
    name: String,
    multiple: { type: Boolean, default: false },
    options: { type: Array, default: [] },
    dependsOn: { type: String, default: "" },
    placeholder: { type: String, default: "Szukaj…" },
    disabledPlaceholder: { type: String, default: "Najpierw wybierz pole nadrzędne" },
    max: { type: Number, default: 0 },
    required: { type: Boolean, default: false },
    url: { type: String, default: "" },
    minChars: { type: Number, default: 2 },
    freeText: { type: Boolean, default: false },
    initial: { type: Array, default: [] },
  }

  connect() {
    this.selected = []
    this.hi = 0
    this.groupFilter = null
    this._remoteResults = []
    this._remoteError = false
    this._fetchSeq = 0
    this._debounceTimer = null
    // Store closest form once for reset listener and scope guard — element doesn't move after connect.
    this._form = this.element.closest("form")
    this._formResetListener = null
    if (this._form) {
      this._formResetListener = () => this.clearAll()
      this._form.addEventListener("reset", this._formResetListener)
    }
    if (this.dependsOnValue) {
      this.onParentChange = (e) => {
        if (e.detail.name !== this.dependsOnValue) return
        // Scope guard: if both comboboxes live in a form and they differ, the event belongs
        // to a different form — ignore it to prevent cross-form cascade clearing.
        // If either side has no form, fall back to the old name-only behaviour.
        const theirForm = e.detail.scope ?? null
        if (this._form && theirForm && this._form !== theirForm) return
        this.groupFilter = e.detail.value || null
        // Pass the silent flag through: a silent parent preselect (URL restore) must not
        // re-emit a county:change (which would trigger the catalog form to submit).
        this.clearAll({ silent: !!e.detail.silent })
        this.refreshDisabled()
      }
      window.addEventListener("combobox:change", this.onParentChange)
      // Bootstrap from the parent's CURRENT hidden input value at connect time.
      // The parent may have already fired its silent initial preselect before we connected
      // (Stimulus connect order is DOM order — parent first, but only if defined before us).
      // Reading the hidden input catches the value regardless of event timing.
      if (this._form) {
        const parentHidden = this._form.querySelector(
          `input[type="hidden"][name="${this.dependsOnValue}"]`
        )
        if (parentHidden?.value) {
          const parsed = parseInt(parentHidden.value, 10)
          this.groupFilter = isNaN(parsed) ? parentHidden.value : parsed
        }
      }
    }
    this.refreshDisabled()
    this.renderChips()
    // Preselect from data-combobox-initial-value on connect (server-supplied chips).
    // Silent: dependents react via onParentChange but no form submit fires.
    if (this.initialValue.length) this.selectValues(this.initialValue, { silent: true })
  }

  disconnect() {
    if (this.onParentChange) window.removeEventListener("combobox:change", this.onParentChange)
    if (this._form && this._formResetListener) {
      this._form.removeEventListener("reset", this._formResetListener)
    }
    clearTimeout(this._debounceTimer)
  }

  // Called by Stimulus when optionsValue attribute changes (including on first connect).
  // Re-renders chips keeping only selections whose value still exists in the new options.
  // Never emits — options injection is infrastructure, not a user interaction.
  optionsValueChanged() {
    if (!this.selected) return // guard: called before connect initialises selected
    if (!this.urlValue) {
      const newOpts = this.optionsValue
      this.selected = this.selected.filter(s =>
        newOpts.some(o => String(o.value) === String(s.value))
      )
    }
    this.renderChips()
    // Options that arrive while the list is open (focus before the API answered) must show at once.
    if (!this.urlValue && !this.listTarget.hidden) this.renderList(true)
  }

  // ---- stan pomocniczy ----
  get isDisabled() { return !!this.dependsOnValue && !this.groupFilter }
  get pool() {
    let opts = this.optionsValue
    if (this.dependsOnValue) opts = opts.filter(o => o.group === this.groupFilter)
    return opts.filter(o => !this.selected.some(s => s.value === o.value))
  }
  norm(s) { return s.toLowerCase().replace(/ł/g, "l").normalize("NFD").replace(/[̀-ͯ]/g, "") }
  results() {
    if (this.urlValue) return this._remoteResults
    const q = this.norm(this.inputTarget.value.trim())
    if (!q) return this.pool.slice(0, 8)
    const matches = this.pool.filter(o => this.norm(o.label).includes(q))
    // Rank: starts-with before contains, preserving original order within each group.
    const starts = matches.filter(o => this.norm(o.label).startsWith(q))
    const rest   = matches.filter(o => !this.norm(o.label).startsWith(q))
    return [...starts, ...rest].slice(0, 8)
  }

  // ---- akcje (data-action) ----
  focusInput() { if (!this.isDisabled) this.inputTarget.focus() }
  open() {
    this.hi = 0
    // W trybie zdalnym nie otwieramy pustej listy przy fokusie — czekamy na wpisanie
    if (this.urlValue && !this._remoteResults.length) return
    this.renderList(true)
  }
  close() {
    setTimeout(() => {
      this.listTarget.hidden = true
      if (this.freeTextValue) this._updateFreeTextHidden()
    }, 140)
  }
  search() {
    // free-text: każdy keystroke synchronizuje hidden input z aktualnym tekstem
    if (this.freeTextValue) this._updateFreeTextHidden()
    if (this.urlValue) {
      clearTimeout(this._debounceTimer)
      const q = this.inputTarget.value.trim()
      if (q.length < this.minCharsValue) {
        this._fetchSeq += 1
        this._remoteResults = []
        this.renderList(false)
        return
      }
      this._debounceTimer = setTimeout(() => this._fetchRemote(q), 200)
      return
    }
    this.hi = 0
    this.renderList(true)
  }
  // Przestawia podświetlenie na istniejących wierszach — bez przebudowy listy.
  highlight(index) {
    this.hi = index
    const rows = this.listTarget.querySelectorAll(".ls-combo__opt")
    rows.forEach((row, i) => row.classList.toggle("is-hi", i === index))
  }
  keydown(e) {
    const res = this.results()
    if (e.key === "ArrowDown") { e.preventDefault(); this.highlight(Math.min(this.hi + 1, res.length - 1)) }
    else if (e.key === "ArrowUp") { e.preventDefault(); this.highlight(Math.max(this.hi - 1, 0)) }
    else if (e.key === "Enter") { e.preventDefault(); if (!this.listTarget.hidden && res[this.hi]) this.pick(res[this.hi]) }
    else if (e.key === "Escape") { this.listTarget.hidden = true }
    else if (e.key === "Backspace" && !this.inputTarget.value && this.selected.length) {
      this.remove(this.selected[this.selected.length - 1].value)
    }
  }

  // ---- remote fetch (tryb zdalny) ----
  async _fetchRemote(q) {
    const seq = ++this._fetchSeq
    const url = new URL(this.urlValue, location.href)
    url.searchParams.set("q", q)
    // Wartość rodzica (dependsOn) jako parametr filtrujący
    if (this.dependsOnValue && this.groupFilter != null) {
      url.searchParams.set(this.dependsOnValue, String(this.groupFilter))
    }
    try {
      const data = await getJSON(url.toString())
      if (seq !== this._fetchSeq) return // stale response guard
      const raw = Array.isArray(data) ? data : (data?.results ?? [])
      this._remoteResults = raw.slice(0, 20).map(r => ({
        value: r.value ?? r.id,
        label: r.label ?? r.name ?? String(r.value ?? r.id),
        meta: r.meta ?? null,
        group: r.group ?? null,
      }))
      this._remoteError = false
      this.hi = 0
      this.renderList(true)
    } catch {
      if (seq !== this._fetchSeq) return
      this._remoteResults = []
      this._remoteError = true
      this.renderList(true)
    }
  }

  // ---- logika wyboru ----
  pick(opt) {
    this._fetchSeq += 1
    clearTimeout(this._debounceTimer)
    if (this.freeTextValue) {
      // free-text: sugestia wypełnia input tekstem (nie PK); lista zamyka się
      this.inputTarget.value = opt.label
      this._updateFreeTextHidden()
      this.listTarget.hidden = true
      this._remoteResults = []
      this.emit()
      return
    }
    if (this.multipleValue && this.maxValue && this.selected.length >= this.maxValue) return
    this.selected = this.multipleValue ? [...this.selected, opt] : [opt]
    this.inputTarget.value = ""
    this.renderChips()
    this.renderList(this.multipleValue) // multiselect: lista zostaje otwarta
    if (!this.multipleValue) this.inputTarget.blur()
    this.emit()
  }
  remove(value) {
    this.selected = this.selected.filter(s => s.value !== value)
    this.renderChips()
    this.emit()
    if (!this.multipleValue) this.inputTarget.focus()
  }
  clearAll({ silent = false } = {}) {
    this._fetchSeq += 1
    clearTimeout(this._debounceTimer)
    this.selected = []
    this.inputTarget.value = ""
    this._remoteResults = []
    this._remoteError = false
    if (this.freeTextValue) {
      this._updateFreeTextHidden()
    } else {
      this.renderChips()
    }
    this.renderList(false)
    if (!silent) this.emit()
  }

  // Zaznaczenie z zewnątrz po wartościach (np. wypełnienie formularza edycji z API) —
  // mapuje id-ki na opcje z optionsValue (już wczytane server-side, bez fetcha), zastępuje
  // cały wybór, renderuje chipy i emituje combobox:change tak jak pick()/clearAll().
  // Nieznane wartości (spoza optionsValue) są pomijane.
  //
  // silent: true — preselect without dispatching combobox:change; prevents infinite reload
  // when restoring URL params on page load (no form submission should fire).
  //
  // Remote mode (urlValue set): values must be {value, label} objects — there is no local
  // options list to look up, so the caller provides the label directly. Used to show the
  // county chip restored from the URL without triggering a search.
  selectValues(values, { silent = false } = {}) {
    const list = Array.isArray(values) ? values : []
    let found
    if (this.urlValue) {
      // Remote mode: accept {value, label} objects directly — no options list to search.
      found = list.filter(v => v && typeof v === "object" && v.value != null && v.label != null)
    } else {
      // Local mode: look up each value (PK or {value} object) in optionsValue.
      found = list
        .map(v => {
          const search = (typeof v === "object" && v !== null) ? String(v.value) : String(v)
          return this.optionsValue.find(o => String(o.value) === search)
        })
        .filter(Boolean)
    }
    this.selected = this.multipleValue ? found : found.slice(0, 1)
    this.inputTarget.value = ""
    this.renderChips()
    if (!silent) {
      this.emit()
    } else {
      // Silent preselect still notifies dependent comboboxes (e.g. county after voivodeship
      // URL restore) so they can enable themselves via onParentChange.  The catalog submit
      // listener recognises detail.silent === true and does NOT trigger a form submit.
      const value = this.multipleValue
        ? this.selected.map(s => s.value)
        : (this.selected[0]?.value ?? null)
      window.dispatchEvent(new CustomEvent("combobox:change", {
        detail: { name: this.nameValue, value, scope: this.element.closest("form"), silent: true },
      }))
    }
  }
  emit() {
    let value
    if (this.freeTextValue) {
      value = this.inputTarget.value.trim() || null
    } else {
      value = this.multipleValue ? this.selected.map(s => s.value) : (this.selected[0]?.value ?? null)
    }
    // scope: the closest <form> element, or null when the combobox lives outside any form.
    // Listeners use this to ignore events from a different form (cross-form cascade prevention).
    window.dispatchEvent(new CustomEvent("combobox:change", {
      detail: { name: this.nameValue, value, scope: this.element.closest("form") }
    }))
  }

  // ---- free-text hidden input sync ----
  // Zsynchronizuje jeden hidden input z bieżącym tekstem pola — wywołana przy każdej
  // zmianie input.value (search, pick, close, clearAll) żeby formularz zawsze miał
  // aktualną wartość city_text niezależnie od tego jak user ją wpisał.
  _updateFreeTextHidden() {
    this.hiddenTarget.textContent = ""
    const text = this.inputTarget.value.trim()
    if (text) {
      const hid = document.createElement("input")
      hid.type = "hidden"
      hid.name = this.nameValue
      hid.value = text
      this.hiddenTarget.appendChild(hid)
    }
  }

  // ---- render ----
  refreshDisabled() {
    const dis = this.isDisabled
    this.inputTarget.disabled = dis
    this.boxTarget.classList.toggle("is-disabled", dis)
    this.inputTarget.placeholder = dis ? this.disabledPlaceholderValue : this.placeholderValue
  }

  renderChips() {
    if (this.freeTextValue) {
      // free-text: brak chipsów — hidden input synchronizuje się z tekstem pola
      this._updateFreeTextHidden()
      return
    }
    // Czyścimy zawartość chipów i hidden inputów — textContent zamiast innerHTML (XSS)
    this.chipsTarget.textContent = ""
    this.hiddenTarget.textContent = ""
    for (const s of this.selected) {
      const chip = document.createElement("span")
      chip.className = "ls-chip"

      const labelSpan = document.createElement("span")
      labelSpan.className = "ls-chip__label"
      labelSpan.textContent = s.label
      chip.appendChild(labelSpan)

      if (s.meta) {
        const metaSpan = document.createElement("span")
        metaSpan.className = "ls-chip__meta"
        metaSpan.textContent = s.meta
        chip.appendChild(metaSpan)
      }

      const xBtn = document.createElement("button")
      xBtn.type = "button"
      xBtn.className = "ls-chip__x"
      xBtn.setAttribute("aria-label", "Usuń")
      xBtn.textContent = "×"
      xBtn.addEventListener("mousedown", (e) => { e.preventDefault() })
      xBtn.addEventListener("click", () => this.remove(s.value))
      chip.appendChild(xBtn)

      this.chipsTarget.appendChild(chip)

      const hid = document.createElement("input")
      hid.type = "hidden"
      hid.name = this.nameValue
      hid.value = s.value
      this.hiddenTarget.appendChild(hid)
    }
    const singleSelected = !this.multipleValue && this.selected.length > 0
    this.boxTarget.classList.toggle("is-selected", singleSelected)
    this.inputTarget.hidden = singleSelected // chip zastępuje input w trybie single
    if (!this.isDisabled) {
      this.inputTarget.placeholder = this.selected.length ? "Dodaj kolejną…" : this.placeholderValue
    }
    if (!this.multipleValue && !this.isDisabled) {
      this.inputTarget.placeholder = this.placeholderValue
    }
  }

  renderList(show) {
    const res = this.results()
    // Czyścimy listę — textContent zamiast innerHTML (XSS)
    this.listTarget.textContent = ""
    res.forEach((o, i) => {
      const row = document.createElement("div")
      row.className = "ls-combo__opt" + (i === this.hi ? " is-hi" : "")

      const labelSpan = document.createElement("span")
      labelSpan.className = "ls-combo__opt-label"
      labelSpan.textContent = o.label
      row.appendChild(labelSpan)

      if (o.meta) {
        const metaSpan = document.createElement("span")
        metaSpan.className = "ls-combo__meta"
        metaSpan.textContent = o.meta
        row.appendChild(metaSpan)
      }

      row.addEventListener("mousedown", (e) => { e.preventDefault(); this.pick(o) })
      // Najechanie TYLKO przestawia podświetlenie. Przebudowa listy tutaj podmieniałaby
      // węzeł pod kursorem, więc mousedown trafiał w element odpięty od DOM i wybór ginął.
      row.addEventListener("mouseenter", () => this.highlight(i))
      this.listTarget.appendChild(row)
    })
    if (!res.length) {
      const empty = document.createElement("div")
      empty.className = "ls-combo__empty"
      empty.textContent = (this.urlValue && this._remoteError)
        ? "Nie udało się pobrać podpowiedzi."
        : "Brak wyników"
      this.listTarget.appendChild(empty)
    }
    this.listTarget.hidden = !show || this.isDisabled
  }
}
